import type { FC, ReactElement } from "react"

import {
    Button,
    EButtonSize,
    ETextAreaScrollbarVariant,
    ETextAreaVariant,
    ETypographyColor,
    ETypographySize,
    ETypographyWeight,
    ErrorHint,
    TextArea,
    Typography
} from "@/3514/components"
import { withDynamicHeightGuard } from "@/3514/guards"
import { EFeedbackModalFormField as EFormField } from "@/3514/modals/feedback/types"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { emptyAsyncCallback } from "@/shared/types/functions"

import { useCoachingChatMatrixFeedbackModalContext as useModalContext } from "../../context"
import { coachingChatStyles as chatStyles } from "../../styles"

import { CoachingChatMatrixFeedbackModalChart as Chart } from "./coaching-chat.matrix-feedback.modal.chart.component"

const {
    modals: { matrix: classes }
} = chatStyles

const Body: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        chart: { isSubmittable: isChartSubmittable },
        form: {
            onSubmit: onFormSubmit,
            onChange: onInputChange,
            onKeyDown: onInputKeyDown,
            inputRef,
            isValid: isFormValid
        },
        mutation
    }: ReturnType<typeof useModalContext> = useModalContext()

    return (
        <div className={classes.modalBodyContainer}>
            <div className={classes.modalBodyWrapper}>
                <Chart />
            </div>

            <div className={classes.modalBodyWrapperFormWrapper}>
                <form onSubmit={emptyAsyncCallback} className={classes.modalBodyWrapperForm}>
                    <Typography
                        className={classes.modalBodyWrapperFormLabel}
                        size={ETypographySize.Small}
                        weight={ETypographyWeight.Bold}
                        text={t("entities.coachingChat.chat.matrixFeedback.modal.form.title")}
                    />
                    <TextArea
                        ref={inputRef}
                        name={EFormField.Feedback}
                        variant={ETextAreaVariant.Transparent}
                        textColor={ETypographyColor.Black}
                        textSize={ETypographySize.Medium}
                        placeholderTextSize={ETypographySize.Medium}
                        className={classes.modalBodyWrapperFormInput}
                        onKeyDown={onInputKeyDown}
                        onChange={onInputChange}
                        withNoBorder
                        borderRadius={{ tl: 10, tr: 10, bl: 10, br: 10 }}
                        minLines={2}
                        maxSymbols={1024}
                        scrollbarVariant={ETextAreaScrollbarVariant.Thin}
                        withNoResize
                        withItalicPlaceholder
                        placeholder={t("entities.coachingChat.chat.matrixFeedback.modal.form.placeholder")}
                        enterKeyHint="done"
                    />
                </form>

                <ErrorHint isVisible={mutation.isError} />
            </div>

            <Button
                type="button"
                isCentered
                isDisabled={mutation.isError || !isChartSubmittable || !isFormValid}
                isBusy={mutation.isPending}
                size={EButtonSize.Small}
                onClick={withPressHandler(onFormSubmit)}
                onKeyDown={withPressHandler(onFormSubmit)}
                minWidth={140}
            >
                {t("common.submit")}
            </Button>
        </div>
    )
}

Body.displayName = "CoachingChatMatrixFeedbackModalBody"

const GuardedBody: FC = withDynamicHeightGuard(Body)

export { GuardedBody as CoachingChatMatrixFeedbackModalBody }
